import { type ComboboxProps } from "@vega-core/components/shadcn/combobox";

import {
  canCreateOption,
  hasExactItemMatch,
  hasExactSelectedMatch,
  makeCreateOption,
  mergeItemsWithSelected,
  normalizeSelectedItems,
} from "../helpers";
import { type SearchMultiSelectItem } from "../types";

type OnValueChange<
  Value extends SearchMultiSelectItem,
  Multiple extends boolean | undefined,
> = NonNullable<ComboboxProps<Value, Multiple>["onValueChange"]>;

type OnValueChangeParams<
  Value extends SearchMultiSelectItem,
  Multiple extends boolean | undefined,
> = Parameters<OnValueChange<Value, Multiple>>;

type SelectedParam<
  Value extends SearchMultiSelectItem,
  Multiple extends boolean | undefined,
> = OnValueChangeParams<Value, Multiple>[0];

type EventDetailsParam<
  Value extends SearchMultiSelectItem,
  Multiple extends boolean | undefined,
> = OnValueChangeParams<Value, Multiple>[1];

type UseCreatableParams<
  Value extends SearchMultiSelectItem,
  Multiple extends boolean | undefined,
> = {
  items: ComboboxProps<Value, Multiple>["items"];
  selectedValues: ComboboxProps<Value, Multiple>["value"];
  trimmedInputValue: string;
  creatable: boolean;
  multiple?: Multiple;
  onSelectionChange?: ComboboxProps<Value, Multiple>["onValueChange"];
};

export function useCreatable<
  Value extends SearchMultiSelectItem,
  Multiple extends boolean | undefined,
>({
  items,
  selectedValues,
  trimmedInputValue,
  creatable,
  multiple,
  onSelectionChange,
}: UseCreatableParams<Value, Multiple>) {
  const availableItems = Array.from(items ?? []);
  const selectedItems = normalizeSelectedItems(selectedValues);
  const exactItemMatch = hasExactItemMatch(availableItems, trimmedInputValue);
  const exactSelectedMatch = hasExactSelectedMatch(
    selectedItems,
    trimmedInputValue,
  );
  const createOption = canCreateOption(
    creatable,
    trimmedInputValue,
    exactItemMatch,
    exactSelectedMatch,
  )
    ? makeCreateOption<Value>(trimmedInputValue)
    : null;
  const itemsWithSelectedValues = mergeItemsWithSelected(
    availableItems,
    selectedItems,
  );
  const renderedItems = createOption
    ? [...itemsWithSelectedValues, createOption]
    : itemsWithSelectedValues;

  const emitSelection = (
    value: SelectedParam<Value, Multiple>,
    eventDetails?: EventDetailsParam<Value, Multiple>,
  ) => {
    const callback = onSelectionChange as
      | ((
          value: SelectedParam<Value, Multiple>,
          eventDetails?: EventDetailsParam<Value, Multiple>,
        ) => void)
      | undefined;

    callback?.(value, eventDetails);
  };

  const handleCreateOptionSelect = (
    value: string,
    eventDetails?: EventDetailsParam<Value, Multiple>,
  ) => {
    const customOption = makeCreateOption<Value>(value);

    if (multiple) {
      const values = Array.isArray(selectedValues) ? selectedValues : [];
      emitSelection(
        [...values, customOption] as SelectedParam<Value, Multiple>,
        eventDetails,
      );

      return;
    }

    emitSelection(customOption as SelectedParam<Value, Multiple>, eventDetails);
  };

  const handleSelectionChange: OnValueChange<Value, Multiple> = (
    selected,
    eventDetails,
  ) => {
    const createOptionId = createOption?.id;

    if (!createOptionId) {
      emitSelection(selected, eventDetails);

      return;
    }

    if (Array.isArray(selected)) {
      if (selected.some((item) => item.id === createOptionId)) {
        handleCreateOptionSelect(createOptionId, eventDetails);

        return;
      }

      emitSelection(
        selected.filter((item) => item.id !== createOptionId) as SelectedParam<
          Value,
          Multiple
        >,
        eventDetails,
      );

      return;
    }

    const selectedSingle = selected as Value | null;

    if (selectedSingle?.id === createOptionId) {
      handleCreateOptionSelect(createOptionId, eventDetails);

      return;
    }

    emitSelection(selected, eventDetails);
  };

  return {
    createOption,
    renderedItems,
    handleSelectionChange,
  };
}
